import { Dialog, Grid, pxToRem, useStyles } from '@archipro-design/aria';
import { CloseLine } from '@archipro-design/icons';
import type {
    LibraryDiagram,
    LibraryImage,
    LibraryVideo,
} from '@modules/product/type/product-gallery-types';
import { isLibraryVideo } from '@modules/product/util/is-library-video';

import { Check, ExternalLink } from 'lucide-react';
import ImageSlide from './ImageSlide';
import CircleMagnifier from './CircleMagnifier';
import * as S from './PhotoViewerModal.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import React from 'react';
import { useInViewport } from 'ahooks';
import { decodeEntities } from '~/utils';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

interface PlainHook {
    type: 'plain';
    text: string;
}
interface NamedHook {
    type: 'named';
    before: string;
    after: string;
}
type CtaHook = PlainHook | NamedHook;

/** Hooks that don't reference the product name */
const PLAIN_HOOKS: PlainHook[] = [
    { type: 'plain', text: 'Like this product?' },
    { type: 'plain', text: 'Inspired by what you see?' },
    { type: 'plain', text: 'Want to know more?' },
    { type: 'plain', text: 'Love what you see?' },
    { type: 'plain', text: 'Ready to take the next step?' },
];

/** Hooks that embed the product name as a clickable link */
const NAMED_HOOKS: NamedHook[] = [
    { type: 'named', before: 'See yourself living with', after: '?' },
    { type: 'named', before: 'Ready to bring', after: 'home?' },
    { type: 'named', before: 'Picture', after: 'in your space?' },
    { type: 'named', before: 'Could', after: 'be the perfect fit?' },
    { type: 'named', before: 'Discover more about', after: '' },
];

const ALL_HOOKS: CtaHook[] = [...PLAIN_HOOKS, ...NAMED_HOOKS];

interface PhotoViewerModalProps {
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>;
    selectedSlide: number;
    open: boolean;
    toggle: () => void;
    itemID?: number;
    title?: string;
    /** Optional CTA configuration for the bottom section */
    cta?: {
        /** External website URL with tracking params already applied */
        websiteUrl?: string;
        /** Additional tracking params for website link tracking */
        websiteTrackingParams?: Record<string, string | number | undefined>;
        /** Callback to open enquiry modal */
        onEnquire?: () => void;
        /** Label for enquiry button */
        enquiryLabel?: string;
    };
    /** Fired when the focused photo changes, with the original stack index */
    onSlideChange?: (stackIndex: number) => void;
}

const MODAL_IMAGE_MAX_WIDTH = 1768;
const MODAL_IMAGE_MAX_HEIGHT = 780;
const BM_PRODUCT_IMG_MIN_WIDTH = 1000;
const BM_PRODUCT_IMG_MIN_HEIGHT = 1000;
/**
 * Treat pale grey backgrounds as near-white so the dark comparison handle is
 * used unless the background provides strong contrast for the white handle.
 */
const NEAR_WHITE_RGB_THRESHOLD = 235;

/**
 * Calculates the actual displayed image dimensions when using objectFit: contain
 * within a bounding box. This ensures the overlay matches the visible image area
 * rather than the larger container with whitespace.
 */
const getContainedImageSize = (
    aspectRatio: number,
    maxWidth: number,
    maxHeight: number,
): { width: number; height: number } => {
    const containerAR = maxWidth / maxHeight;
    if (aspectRatio >= containerAR) {
        return { width: maxWidth, height: Math.round(maxWidth / aspectRatio) };
    }
    return { width: Math.round(maxHeight * aspectRatio), height: maxHeight };
};

const getBackgroundColorRgb = (
    backgroundColor?: string | null,
): [number, number, number] | null => {
    if (!backgroundColor) {
        return null;
    }

    const trimmedBackgroundColor = backgroundColor.trim();

    if (/^#([\da-f]{3}|[\da-f]{6})$/i.test(trimmedBackgroundColor)) {
        const hex = trimmedBackgroundColor.slice(1);
        const normalisedHex =
            hex.length === 3
                ? hex
                      .split('')
                      .map((channel) => channel + channel)
                      .join('')
                : hex;

        return [
            parseInt(normalisedHex.slice(0, 2), 16),
            parseInt(normalisedHex.slice(2, 4), 16),
            parseInt(normalisedHex.slice(4, 6), 16),
        ];
    }

    const rgbMatch = trimmedBackgroundColor.match(
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
    );
    if (!rgbMatch) {
        return null;
    }

    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
};

const isNearWhiteBackgroundColor = (
    backgroundColor?: string | null,
): boolean => {
    const rgb = getBackgroundColorRgb(backgroundColor);
    if (!rgb) {
        return false;
    }

    return rgb.every((channel) => channel >= NEAR_WHITE_RGB_THRESHOLD);
};

const PhotoViewerModal = ({
    stack,
    selectedSlide,
    open,
    toggle,
    itemID,
    title,
    cta,
    onSlideChange,
}: PhotoViewerModalProps) => {
    const { css } = useStyles();
    const { hasVisited, markVisited } = useWebsiteVisited(itemID);
    const { hasSubmitted } = useEnquirySubmitted(itemID);
    /** Matches Aria Image's background for multiply blend - use explicit hex to avoid theme resolving to white */
    const multipliedImageBg = '#F2F1F0';

    /** First photo in the stack, rendered as a thumbnail in the CTA section */
    const firstPhoto = React.useMemo(() => {
        const slide = stack.find((s) => !isLibraryVideo(s));
        if (!slide) return null;
        const src =
            slide.__typename === 'LibraryImage' ||
            slide.__typename === 'DiagramImage'
                ? (slide.LibraryImageFile?.ThumbCategory ?? '')
                : '';
        if (!src) return null;
        const isMultiplied =
            slide.__typename === 'LibraryImage' && !!slide.IsMultiplied;
        const isClearCut =
            slide.__typename === 'LibraryImage' && !!slide.IsClearCut;
        return {
            src,
            isMultiplied,
            isClearCut,
            backgroundColor:
                !isMultiplied && isClearCut
                    ? (slide.LibraryImageFile?.ImageBackgroundColor ??
                      '#F2F1F0')
                    : '#F2F1F0',
        };
    }, [stack]);

    const canUseNamedHooks = Boolean(title && cta?.websiteUrl);
    const defaultHook: CtaHook = PLAIN_HOOKS[0]!;

    const pickRandom = (pool: CtaHook[]): CtaHook =>
        pool[Math.floor(Math.random() * pool.length)] ?? defaultHook;

    const [ctaHook, setCtaHook] = React.useState<CtaHook>(defaultHook);
    React.useEffect(() => {
        if (open) {
            const pool = canUseNamedHooks ? ALL_HOOKS : PLAIN_HOOKS;
            setCtaHook(pickRandom(pool));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, canUseNamedHooks]);

    const containerRef = React.useRef(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<(HTMLDivElement | null)[]>([]);
    const ctaSectionRef = React.useRef<HTMLDivElement>(null);
    const hasPlayedEntranceRef = React.useRef(false);
    const [thumbnailLifted, setThumbnailLifted] = React.useState(false);
    const isScrollingRef = React.useRef(false);
    const lastScrollTimeRef = React.useRef(0);
    const [isAtCtaSection, setIsAtCtaSection] = React.useState(false);

    const [inViewport] = useInViewport(containerRef, {
        threshold: 0,
        rootMargin: '100%',
    });

    /** Maps sequential photo position (0-based) → original stack index, skipping videos */
    const photoIndices = React.useMemo(
        () =>
            stack.reduce<number[]>((acc, slide, idx) => {
                if (!isLibraryVideo(slide)) acc.push(idx);
                return acc;
            }, []),
        [stack],
    );
    const photoCount = photoIndices.length;
    const [currentIndex, setCurrentIndex] = React.useState(() => {
        const pos = photoIndices.indexOf(selectedSlide);
        return (pos !== -1 ? pos : 0) + 1;
    });
    const shouldShowScrollHint = selectedSlide === 0 && photoCount > 1;
    const [scrollHintVisible, setScrollHintVisible] = React.useState(false);

    // Fade in scroll hint after a brief delay, then fade out after 3 seconds
    React.useEffect(() => {
        if (!shouldShowScrollHint) return;

        // Fade in after a short delay
        const fadeInTimer = setTimeout(() => {
            setScrollHintVisible(true);
        }, 300);

        // Fade out after 3 seconds (plus the fade-in delay)
        const fadeOutTimer = setTimeout(() => {
            setScrollHintVisible(false);
        }, 3300);

        return () => {
            clearTimeout(fadeInTimer);
            clearTimeout(fadeOutTimer);
        };
    }, [shouldShowScrollHint]);

    // Fade out scroll hint when user scrolls away from first photo
    React.useEffect(() => {
        if (currentIndex > 1) {
            setScrollHintVisible(false);
        }
    }, [currentIndex]);

    React.useEffect(() => {
        const stackIndex = photoIndices[currentIndex - 1];
        if (stackIndex !== undefined) {
            onSlideChange?.(stackIndex);
        }
    }, [currentIndex, photoIndices, onSlideChange]);

    const slides = stack.flatMap((slide, idx) => {
        if (isLibraryVideo(slide)) return [];

        const libraryImageSrc =
            slide.__typename === 'LibraryImage' ||
            slide.__typename === 'DiagramImage'
                ? (slide.LibraryImageFile?.ThumbCategory ?? '')
                : '';

        const caption = decodeEntities(slide.Caption ?? '');
        const alt = slide.AltText || `${title} gallery detail image`;

        if (slide.__typename === 'LibraryImage') {
            const imageSize = getContainedImageSize(
                slide.AspectRatio,
                MODAL_IMAGE_MAX_WIDTH,
                MODAL_IMAGE_MAX_HEIGHT,
            );

            const isMultiplied = !!slide.IsMultiplied;
            const shouldShowBlackHandle = isNearWhiteBackgroundColor(
                slide.LibraryImageFile?.ImageBackgroundColor,
            );
            const isClearCut = !!slide.IsClearCut;
            /**
             * Multiplied images must always blend against #F2F1F0 to match the
             * Aria Image component (which hard-codes white['200A'] for multiply).
             * For non-multiplied clearCut images the detected background colour
             * is more appropriate since no blend mode is applied.
             */
            const comparisonBg =
                !isMultiplied && isClearCut
                    ? (slide.LibraryImageFile?.ImageBackgroundColor ??
                      multipliedImageBg)
                    : multipliedImageBg;

            return {
                key: `${idx}-ImageSlide`,
                id: slide.ID,
                idx,
                content: (
                    <div
                        className="relative mx-auto flex items-center justify-center overflow-hidden rounded-[10px]"
                        style={{
                            width: pxToRem(MODAL_IMAGE_MAX_WIDTH),
                            height: pxToRem(MODAL_IMAGE_MAX_HEIGHT),
                            maxWidth: '100%',
                        }}
                    >
                        {!isMultiplied && (
                            <img
                                src={libraryImageSrc}
                                alt=""
                                aria-hidden
                                draggable={false}
                                className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
                                style={{ filter: 'blur(50px)' }}
                            />
                        )}
                        <CircleMagnifier
                            src={libraryImageSrc}
                            alt={alt}
                            imageAspectRatio={slide.AspectRatio}
                        >
                            <div ref={(ref) => { contentRef.current[idx] = ref; }}>
                                <BoostResolutionOverlay
                                    libraryImageId={slide.ID}
                                    currentImageUrl={libraryImageSrc}
                                    displayWidth={imageSize.width}
                                    displayHeight={imageSize.height}
                                    expectedWidth={BM_PRODUCT_IMG_MIN_WIDTH}
                                    expectedHeight={BM_PRODUCT_IMG_MIN_HEIGHT}
                                    wasUpscaled={!!slide.WasUpscaled}
                                    multiplied={isMultiplied}
                                    shouldShowBlackHandle={
                                        shouldShowBlackHandle
                                    }
                                    backgroundColor={comparisonBg}
                                    className="mx-auto w-fit"
                                >
                                    <ImageSlide
                                        key={slide.ID}
                                        libraryImage={libraryImageSrc}
                                        height={1}
                                        width={slide.AspectRatio}
                                        isMultiplied={slide.IsMultiplied}
                                        containerHeight={imageSize.height}
                                        containerWidth={imageSize.width}
                                        sizes={getImagesSizes(imageSize.width)}
                                        loadingValue="eager"
                                        objectFit="contain"
                                        objectPosition="center"
                                        alt={alt}
                                    />
                                </BoostResolutionOverlay>
                                {caption && (
                                    <p className="pt-4 text-center">
                                        {caption}
                                    </p>
                                )}
                            </div>
                        </CircleMagnifier>
                    </div>
                ),
            };
        }

        return {
            key: `${idx}-ImageSlide`,
            id: slide.ID,
            idx,
            content: (
                <div
                    className="relative mx-auto flex items-center justify-center overflow-hidden rounded-[10px]"
                    style={{
                        width: pxToRem(MODAL_IMAGE_MAX_WIDTH),
                        height: pxToRem(MODAL_IMAGE_MAX_HEIGHT),
                        maxWidth: '100%',
                    }}
                >
                    {!slide.IsMultiplied && (
                        <img
                            src={libraryImageSrc}
                            alt=""
                            aria-hidden
                            draggable={false}
                            className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
                            style={{ filter: 'blur(50px)' }}
                        />
                    )}
                    <CircleMagnifier
                        src={libraryImageSrc}
                        alt={alt}
                        imageAspectRatio={slide.AspectRatio}
                    >
                        <ImageSlide
                            key={slide.ID}
                            libraryImage={libraryImageSrc}
                            height={1}
                            width={slide.AspectRatio}
                            isMultiplied={slide.IsMultiplied}
                            containerHeight={MODAL_IMAGE_MAX_HEIGHT}
                            containerWidth={MODAL_IMAGE_MAX_WIDTH}
                            sizes={getImagesSizes(MODAL_IMAGE_MAX_WIDTH)}
                            loadingValue="eager"
                            objectFit="contain"
                            objectPosition="center"
                            ref={(ref) => { contentRef.current[idx] = ref; }}
                            caption={caption}
                            alt={alt}
                        />
                    </CircleMagnifier>
                </div>
            ),
        };
    });

    /** Navigate to a specific photo by its sequential position (0-based) */
    const navigateToPhoto = React.useCallback(
        (photoPosition: number) => {
            const clamped = Math.max(
                0,
                Math.min(photoPosition, photoCount - 1),
            );
            const stackIndex = photoIndices[clamped];
            const element =
                stackIndex !== undefined
                    ? contentRef.current[stackIndex]
                    : null;

            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                const now = Date.now();
                const timeSinceLastScroll = now - lastScrollTimeRef.current;
                const cooldown = timeSinceLastScroll < 400 ? 300 : 500;
                lastScrollTimeRef.current = now;

                setTimeout(() => {
                    isScrollingRef.current = false;
                }, cooldown);
            } else {
                isScrollingRef.current = false;
            }
        },
        [photoCount, photoIndices],
    );

    /** Scroll to the CTA section at the bottom */
    const scrollToCtaSection = React.useCallback(() => {
        const ctaElement = ctaSectionRef.current;
        if (ctaElement) {
            ctaElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });

            setTimeout(() => {
                isScrollingRef.current = false;
            }, 500);
        } else {
            isScrollingRef.current = false;
        }
    }, []);

    // Handle wheel events for smooth photo-to-photo navigation
    React.useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const hasCta = cta && (cta.websiteUrl || cta.onEnquire);

        // Minimum delta threshold to filter out small trackpad movements
        const DELTA_THRESHOLD = 30;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();

            // Early exit if already scrolling or delta is too small
            if (isScrollingRef.current) return;
            if (Math.abs(e.deltaY) < DELTA_THRESHOLD) return;

            // Lock scrolling immediately to prevent multiple triggers
            isScrollingRef.current = true;

            const direction = e.deltaY > 0 ? 1 : -1;
            const currentPhotoIndex = currentIndex - 1;

            // If scrolling down from the last photo and there's a CTA section
            if (
                direction === 1 &&
                currentPhotoIndex === photoCount - 1 &&
                hasCta
            ) {
                scrollToCtaSection();
                return;
            }

            // If at CTA section and scrolling up, go back to last photo
            if (direction === -1 && isAtCtaSection) {
                navigateToPhoto(photoCount - 1);
                return;
            }

            // Normal photo navigation
            const nextIndex = currentPhotoIndex + direction;
            if (nextIndex >= 0 && nextIndex < photoCount) {
                navigateToPhoto(nextIndex);
            } else {
                // Reset lock if we didn't navigate (at bounds)
                isScrollingRef.current = false;
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [
        currentIndex,
        photoCount,
        navigateToPhoto,
        scrollToCtaSection,
        isAtCtaSection,
        cta,
    ]);

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!open) return;

            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                navigateToPhoto(currentIndex); // currentIndex is 1-based, so this goes to next
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                navigateToPhoto(currentIndex - 2); // Go to previous (currentIndex - 1 - 1)
            } else if (e.key === 'Escape') {
                toggle();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, currentIndex, navigateToPhoto, toggle]);

    // Track which photo is currently in view using IntersectionObserver
    React.useEffect(() => {
        const elements = contentRef.current.filter(Boolean);
        if (elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const stackIndex = contentRef.current.findIndex(
                            (el) => el === entry.target,
                        );
                        const photoPos = photoIndices.indexOf(stackIndex);
                        if (photoPos !== -1) {
                            setCurrentIndex(photoPos + 1);
                        }
                    }
                });
            },
            {
                threshold: 0.5,
            },
        );

        elements.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [slides.length, photoIndices]);

    // Track when CTA section is in view
    React.useEffect(() => {
        const ctaElement = ctaSectionRef.current;
        if (!ctaElement) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsAtCtaSection(entry.isIntersecting);
                });
            },
            {
                threshold: 0.3,
            },
        );

        observer.observe(ctaElement);
        return () => observer.disconnect();
    }, [cta]);

    // Play thumbnail entrance animation once after the scroll to the CTA section finishes
    React.useEffect(() => {
        if (!isAtCtaSection || hasPlayedEntranceRef.current) return;
        hasPlayedEntranceRef.current = true;
        const liftTimer = setTimeout(() => setThumbnailLifted(true), 300);
        const settleTimer = setTimeout(() => setThumbnailLifted(false), 2500);
        return () => {
            clearTimeout(liftTimer);
            clearTimeout(settleTimer);
        };
    }, [isAtCtaSection]);

    const initialSlideRef = React.useRef(selectedSlide);

    React.useEffect(() => {
        if (!inViewport) return;

        const element = contentRef.current[initialSlideRef.current];
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [inViewport]);

    return (
        <Dialog
            ref={containerRef}
            open={open}
            onCancel={() => toggle()}
            headerAction={{
                icon: (
                    <CloseLine
                        className={css({
                            width: `${pxToRem(40)} !important`,
                            height: `${pxToRem(40)} !important`,
                        })}
                    />
                ),
                title: 'Close',
                onClick: () => toggle(),
            }}
            content={{
                content: (
                    <>
                        <div
                            ref={scrollContainerRef}
                            className="h-screen overflow-y-auto scroll-smooth"
                        >
                            <Grid columns={1} className={css(S.StackContainer)}>
                                {slides.map((s) => {
                                    const activeStackIdx =
                                        photoIndices[currentIndex - 1];
                                    const isActive = s.idx === activeStackIdx;
                                    return (
                                        <div
                                            key={s.key}
                                            className="transition-all duration-500 ease-out"
                                            style={{
                                                transform: isActive
                                                    ? 'scale(1)'
                                                    : 'scale(0.95)',
                                                opacity: isActive ? 1 : 0.4,
                                            }}
                                        >
                                            {s.content}
                                        </div>
                                    );
                                })}
                            </Grid>
                            {/* CTA Section */}
                            {cta && (cta.websiteUrl || cta.onEnquire) && (
                                <div
                                    ref={ctaSectionRef}
                                    className="flex min-h-[50vh] items-center justify-center pb-32 transition-opacity duration-700 ease-out"
                                    style={{
                                        opacity: isAtCtaSection ? 1 : 0,
                                    }}
                                >
                                    <div
                                        className={`flex items-center ${
                                            firstPhoto ? 'gap-0' : 'flex-col'
                                        }`}
                                    >
                                        {firstPhoto && (
                                            <>
                                                <div
                                                    className="h-[220px] w-[220px] shrink-0"
                                                    style={{
                                                        perspective: '800px',
                                                    }}
                                                >
                                                    <a
                                                        href={cta.websiteUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        {...cta.websiteTrackingParams}
                                                        onClick={markVisited}
                                                        className="group relative flex h-[220px] w-[220px] items-center justify-center overflow-hidden rounded-lg transition-all duration-500 ease-out"
                                                        style={{
                                                            backgroundColor:
                                                                firstPhoto.isMultiplied
                                                                    ? firstPhoto.backgroundColor
                                                                    : undefined,
                                                            transform:
                                                                thumbnailLifted
                                                                    ? 'rotateY(0deg) translateZ(12px)'
                                                                    : 'rotateY(-5deg)',
                                                            boxShadow:
                                                                thumbnailLifted
                                                                    ? '0 12px 32px rgba(0,0,0,0.16)'
                                                                    : '8px 6px 20px rgba(0,0,0,0.12)',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform =
                                                                'rotateY(0deg) translateZ(12px)';
                                                            e.currentTarget.style.boxShadow =
                                                                '0 12px 32px rgba(0,0,0,0.16)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform =
                                                                'rotateY(-5deg)';
                                                            e.currentTarget.style.boxShadow =
                                                                '8px 6px 20px rgba(0,0,0,0.12)';
                                                        }}
                                                    >
                                                        {!firstPhoto.isMultiplied && (
                                                            <img
                                                                src={
                                                                    firstPhoto.src
                                                                }
                                                                alt=""
                                                                aria-hidden
                                                                draggable={
                                                                    false
                                                                }
                                                                className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover"
                                                                style={{
                                                                    filter: 'blur(50px)',
                                                                }}
                                                            />
                                                        )}
                                                        <img
                                                            src={firstPhoto.src}
                                                            alt={
                                                                title
                                                                    ? title.trim()
                                                                    : 'Product'
                                                            }
                                                            className="relative max-h-full max-w-full object-contain"
                                                            style={
                                                                firstPhoto.isMultiplied
                                                                    ? {
                                                                          mixBlendMode:
                                                                              'multiply',
                                                                      }
                                                                    : undefined
                                                            }
                                                        />
                                                    </a>
                                                </div>
                                                <div className="bg-gray-300/40 mx-12 h-28 w-px" />
                                            </>
                                        )}
                                        <div
                                            className={`flex flex-col ${
                                                firstPhoto
                                                    ? 'w-[465px] items-start'
                                                    : 'items-center'
                                            }`}
                                        >
                                            <p className="mb-8 text-[1.7rem] font-extralight leading-snug tracking-wide text-gray-400">
                                                {ctaHook.type === 'named' &&
                                                cta.websiteUrl &&
                                                title ? (
                                                    <>
                                                        {ctaHook.before}{' '}
                                                        <a
                                                            href={
                                                                cta.websiteUrl
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...cta.websiteTrackingParams}
                                                            onClick={
                                                                markVisited
                                                            }
                                                            className="decoration-gray-400/50 text-gray-500 underline underline-offset-4 transition-colors duration-300 hover:text-[#111] hover:decoration-[#111]"
                                                        >
                                                            {title.trim()}
                                                        </a>
                                                        {ctaHook.after?.length >
                                                        0
                                                            ? ` ${ctaHook.after}`
                                                            : ''}
                                                    </>
                                                ) : (
                                                    (ctaHook as PlainHook).text
                                                )}
                                            </p>
                                            <div className="flex gap-4">
                                                {cta.websiteUrl && (
                                                    <a
                                                        href={cta.websiteUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        {...cta.websiteTrackingParams}
                                                        onClick={markVisited}
                                                        className="inline-flex items-center gap-2 border border-gray-300 bg-transparent px-8 py-3 text-sm font-medium uppercase tracking-widest text-gray-600 transition-all duration-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white"
                                                    >
                                                        Learn More
                                                        {hasVisited ? (
                                                            <Check className="text-green-600 h-4 w-4 animate-tick-pop-in" />
                                                        ) : (
                                                            <ExternalLink className="h-3.5 w-3.5" />
                                                        )}
                                                    </a>
                                                )}
                                                {cta.onEnquire && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            toggle();
                                                            cta.onEnquire?.();
                                                        }}
                                                        className="bg-[#000000]! hover:bg-transparent! inline-flex items-center gap-2 border border-[#000000] px-8 py-3 text-sm font-medium uppercase tracking-widest text-white transition-all duration-300 hover:text-[#000000]"
                                                    >
                                                        Enquire
                                                        {hasSubmitted && (
                                                            <Check className="text-green-600 h-4 w-4 animate-tick-pop-in" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {photoCount >= 1 && (
                            <div className="fixed bottom-8 right-[30px] z-30 text-xs font-bold tracking-wider text-gray-500 opacity-50">
                                {currentIndex} / {photoCount}
                            </div>
                        )}
                        {/* Scroll hint indicator */}
                        {shouldShowScrollHint && (
                            <div
                                className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 transform flex-col items-center transition-opacity duration-700 ease-out"
                                style={{ opacity: scrollHintVisible ? 1 : 0 }}
                            >
                                <span className="mb-2 text-xs tracking-wider text-gray-400">
                                    Scroll for more
                                </span>
                                <svg
                                    className="h-6 w-6 animate-bounce text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                    />
                                </svg>
                            </div>
                        )}
                    </>
                ),
            }}
            variables={{
                rootWidth: '100vw',
                rootMaxHeight: '100vh',
                overflow: true,
            }}
            className={css(S.PhotoViewerContainer)}
        />
    );
};

export default PhotoViewerModal;
